import { Component, OnInit } from '@angular/core';
import { AppComponent } from '../app.component';
import { LoginService } from 'app/_services/login.service';
import { CommunicationService } from 'app/_services/communication.service';
import { IconDefinition, faEye, faEyeSlash, faCircleInfo, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { LicensingService } from 'app/_services/licensing.service';
import { MsalService } from '@azure/msal-angular';
import { msConfig } from 'app/ms-config';
import { UtilService } from 'app/_services/util.service';

@Component({
  selector: 'app-login-component',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  //#region Fields & icons
  showPassword: boolean = false;
  type: string = 'password';
  changePassword: boolean = false;
  username: string;
  passwordStrength: string;
  cameFromSettings: boolean = false;
  translationsLoaded: boolean = false;
  feedbackMessage: string = undefined;
  resetPasswordToken: string = undefined;
  faEye: IconDefinition = faEye;
  faEyeSlash: IconDefinition = faEyeSlash;
  faCircleInfo: IconDefinition = faCircleInfo;
  faArrowLeft: IconDefinition = faArrowLeft;
  //#endregion 

  constructor(
    private app: AppComponent,
    private loginService: LoginService,
    private communicationService: CommunicationService,
    private licensingService: LicensingService,
    private msal: MsalService,
    private utilService: UtilService
  ) {
    this.resetPasswordToken = undefined;
    this.communicationService.feedback_login_closeFeedbackDialog$.subscribe(() => {
      this.feedbackMessage = undefined;
    });

    this.communicationService.app_base_or_login_translationsLoaded$.subscribe(() => {
      this.translationsLoaded = true;
    });
  }

  ngOnInit(): void {
    if (!localStorage.getItem('qx_msconfig')) {
      this.utilService.getMsDetails().subscribe({
        next: (data: any) => {
          localStorage.setItem('qx_msconfig', JSON.stringify(data));
          msConfig.client = data.client;
          msConfig.authority = data.authority;
          msConfig.redirecturi = data.redirecturi;
          window.location.href = '/signin';
        }
      })
    }

    this.app.getTranslations();
    if (localStorage.getItem('qx_userId')) {
      this.app.router.navigate(['base']);
      return;
    }

    if (sessionStorage.getItem('resetPassword') != undefined && sessionStorage.getItem('resetPassword').toString() == 'true') {
      this.changePassword = true;
      this.cameFromSettings = true;
      this.username = localStorage.getItem('qx_username');
    }

    let queryParams = this.app.router.parseUrl(this.app.router.url).queryParams;
    let queryValues = [];
    Object.keys(queryParams).forEach(key => {
      queryValues.push({ key: key, value: queryParams[key] });
    });

    if (queryValues.length > 0) {
      let formData = this.app.buildForm(['token'], [queryValues[0].value]);
      this.loginService.validateToken(formData).subscribe({
        next: (data: boolean) => {
          if (data) {
            this.resetPasswordToken = queryValues[0].value;
            this.changePassword = true;
            return;
          }

          this.app.alertError(this.app.translations.ALERT_Error_InvalidToken);
        }, error: (errorLog) => { console.log(errorLog); }
      })
    }
  }

  back(): void {
    if (this.cameFromSettings) {
      this.app.router.navigate(['base']);
      return;
    }

    this.changePassword = false;
    this.resetPasswordToken = undefined;
  }

  validatePassword(newPassword: string): void {
    if (newPassword.length < 8) {
      this.passwordStrength = 'password-weak';
      return;
    }

    if (/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /[0-9]/.test(newPassword))
      this.passwordStrength = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'password-strong' : 'password-medium';
  }

  forgotPassword(username: string): void {
    if (!username) {
      this.app.alertError(this.app.translations.ALERT_Error_UsernameRequired);
      return;
    }

    this.username = username;
    let formData = this.app.buildForm(['username'], [username]);
    this.loginService.forgotPassword(formData).subscribe({
      next: (data: { result: string }) => {
        this.feedbackMessage = data.result;
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  resetPassword(currentPasswordOrUsername: string, newPassword: string, confirmPassword: string): void {
    if (currentPasswordOrUsername.length == 0 && !this.resetPasswordToken) {
      this.app.alertError(this.app.translations.ALERT_Error_CurrentPasswordRequired);
      return;
    }

    if (currentPasswordOrUsername.length == 0 && this.resetPasswordToken) {
      this.app.alertError(this.app.translations.ALERT_Error_UsernameRequired);
      return;
    }

    if (newPassword.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_NewPasswordRequired);
      return;
    }

    if (confirmPassword.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_ConfirmPassword);
      return;
    }

    if (confirmPassword.length > 0 && confirmPassword != newPassword) {
      this.app.alertError(this.app.translations.ALERT_Error_PasswordMisMatch);
      return;
    }

    if (this.passwordStrength == 'password-weak') {
      this.app.alertError(this.app.translations.ALERT_Error_WeakPassword);
      return;
    }

    if (!this.resetPasswordToken) {
      let keys = ['username', 'currentPassword', 'newPassword'];
      let values = [this.username, currentPasswordOrUsername, newPassword];
      let formData = this.app.buildForm(keys, values);
      this.loginService.resetPassword(formData).subscribe({
        next: (data: { result: boolean }) => {
          if (data.result) {
            this.login(this.username, newPassword);
            return;
          }
        }, error: (errorLog) => { console.log(errorLog); }
      })
    }

    let keys = ['token', 'username', 'password'];
    let values = [this.resetPasswordToken, currentPasswordOrUsername, newPassword];
    let formData = this.app.buildForm(keys, values);
    this.loginService.resetForgotPassword(formData).subscribe({
      next: (data: { result: string }) => {
        if (data.result) {
          if (data.result.includes('ERROR')) {
            this.feedbackMessage = data.result;
            return;
          }

          this.username = currentPasswordOrUsername;
          this.login(this.username, newPassword);
        }
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  login(username: string, password: string): void {
    if (username.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_UsernameRequired);
      return;
    }

    if (password.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_PasswordRequired);
      return;
    }

    let formData = this.app.buildForm(['username', 'password'], [username, password]);
    this.loginService.loginUser(formData).subscribe({
      next: (data: { id: number, passwordExpired: string, role: string }) => {
        if (!data) {
          this.app.alertError(this.app.translations.ALERT_Error_WrongCredentials);
          return;
        }

        if (data.passwordExpired == 'EXPIRED') {
          this.username = username;
          this.app.alertInfo(this.app.translations.ALERT_Info_PasswordExpired);
          this.changePassword = true;
          return;
        }

        if (!localStorage.getItem('sessionId')) {
          let stationId = localStorage.getItem('stationId');
          let formData = this.app.buildForm(['userId', 'stationId'], [data.id.toString(), stationId]);
          this.licensingService.createSession(formData).subscribe({
            next: (sessionData: { result: boolean, message: string, sessionId: string, stationId: string }) => {
              if (sessionData.message.includes('ERROR')) {
                this.app.alertError(sessionData.message);
                return;
              }

              if (sessionData.result) {
                localStorage.setItem('sessionId', sessionData.sessionId);
                localStorage.setItem('stationId', sessionData.stationId);
              }

              this.handlePostLogin(data.id.toString(), data.role, username, data.passwordExpired);
            }, error: (errorLog) => { console.log(errorLog); }
          })
        } else
          this.handlePostLogin(data.id.toString(), data.role, username, data.passwordExpired);
      }, error: (errorLog) => { console.log(errorLog); }
    });
  }

  handlePostLogin(userId: string, userRole: string, username: string, passwordExpired: string): void {
    localStorage.setItem('qx_userId', userId);
    localStorage.setItem('qx_username', username);
    localStorage.setItem('qx_userRole', userRole);
    this.app.getTranslations();

    if (localStorage.getItem('qx_dashboard')) {
      this.app.router.navigate(['dashboard-viewer']);
      return;
    }

    if (localStorage.getItem('qx_report')) {
      this.app.router.navigate(['report-viewer']);
      return;
    }

    if (passwordExpired == 'OK' || localStorage.getItem('qx_direct'))
      this.app.router.navigate(['base']);

    if (passwordExpired != 'EXPIRED' && passwordExpired != 'OK' && passwordExpired != 'Note') {
      this.app.alertInfo(passwordExpired);
      setTimeout(() => this.app.router.navigate(['base']), 2000);
      return;
    }
  }

  async loginMS() {
    await this.msal.instance.initialize();
    await this.msal.loginRedirect({ scopes: ['openid', 'profile', 'email'] });
  }
}
